import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FileCheck2,
  FileText,
  ArrowUp,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { getLegalDocument, LEGAL_DOCUMENTS } from '../content/legalDocuments';
import LegalDocumentContent from './LegalDocumentContent';

const LEGAL_ICONS = {
  agreement: FileText,
  privacy: ShieldCheck,
  consent: FileCheck2,
  cookies: FileText,
};

function LegalModal({ documentKey, onClose }) {
  const { t } = useLanguage();
  const legalDocument = getLegalDocument(documentKey);

  useEffect(() => {
    if (!legalDocument) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [legalDocument, onClose]);

  if (!legalDocument) return null;

  const Icon = LEGAL_ICONS[documentKey];

  return (
    <motion.div
      className="fixed inset-0 z-[10004] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm sm:p-6"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`legal-title-${documentKey}`}
        className="relative flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0b1120]/95 text-white shadow-[0_28px_90px_rgba(0,0,0,0.65)] ring-1 ring-white/10 backdrop-blur-2xl"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl"
        />

        <div className="relative flex items-start gap-4 border-b border-white/10 px-5 py-5 sm:px-7">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-indigo-300/20 bg-indigo-400/10 text-indigo-200 shadow-inner">
            <Icon size={22} />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-300">
              {t('legal.title')}
            </p>
            <h2
              id={`legal-title-${documentKey}`}
              className="mt-1 pr-10 text-xl font-bold tracking-tight sm:text-2xl"
            >
              {legalDocument.title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-xl border border-white/10 bg-white/5 p-2 text-gray-300 transition hover:bg-white/10 hover:text-white"
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="relative overflow-y-auto px-5 py-5 text-sm leading-relaxed text-gray-300 sm:px-7 sm:py-6">
          <LegalDocumentContent legalDocument={legalDocument} />
        </div>

        <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-black/10 px-5 py-4 sm:px-7">
          <Link
            to={`/legal/${documentKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="legal-modal-permalink"
          >
            {t('legal.openPage')}
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-white/10 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-white/15"
          >
            {t('common.close')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Footer() {
  const [activeDocument, setActiveDocument] = useState(null);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const { t } = useLanguage();

  useEffect(() => {
    const openDocument = (event) => {
      if (getLegalDocument(event.detail)) setActiveDocument(event.detail);
    };
    window.addEventListener('maxim:open-legal', openDocument);
    return () => window.removeEventListener('maxim:open-legal', openDocument);
  }, []);

  const scrollToTop = () => {
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  return (
    <>
      <footer className="archive-footer">
        <div className="archive-footer__inner page-width">
          <div className="archive-footer__top">
            <div className="archive-footer__about">
              <Link
                to="/"
                onClick={scrollToTop}
                className="archive-footer__brand"
              >
                MaximGun
              </Link>

              <p>{t('footer.note')}</p>
            </div>

            <nav
              aria-label="Footer"
              className="archive-footer__links"
            >
              <Link
                to="/about"
                onClick={scrollToTop}
              >
                {t('nav.about')}
              </Link>

              <Link to="/about#friends">
                {t('footer.friends')}
              </Link>

              <a href="https://old.maximgun.ru/">
                {t('footer.legacy')}
              </a>

              {Object.entries(LEGAL_DOCUMENTS).map(([key, item]) => (
                <Link key={key} to={`/legal/${key}`} onClick={scrollToTop}>
                  {t(`footer.${key}`, item.shortTitle)}
                </Link>
              ))}

              <button
                type="button"
                onClick={scrollToTop}
                aria-label={t('footer.top')}
              >
                <ArrowUp size={13} />
                {t('footer.top')}
              </button>
            </nav>
          </div>

          <div className="archive-footer__bottom">
            <span>© 2025–{currentYear} MaximGun</span>
            <span>{t('footer.disclaimer')}</span>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {activeDocument && (
          <LegalModal
            documentKey={activeDocument}
            onClose={() => setActiveDocument(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
